/**
 * Clinical Admin — a customer-centric clinical operations surface with its
 * own tabset, distinct from the rest of /admin. Two tabs:
 *
 *   • Overview — lightweight navigation to clinical operations surfaces.
 *     Expensive lifecycle reports live under Reporting and run only on demand.
 *   • CRM      — a customer roster; click a row to drill into one patient.
 *
 * Tab state lives in the URL (`?tab=`) so it survives refresh and is
 * shareable.
 */
import { Link, useSearchParams } from "react-router-dom";

import { AgentScreen } from "./AgentScreen";
import { CustomerTable } from "./CustomerTable";

type TabKey = "overview" | "crm" | "agent";

const TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "crm", label: "CRM" },
  { key: "agent", label: "AI Agent" },
];

export function ClinicalAdminScreen() {
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab");
  const tab: TabKey = TABS.some((t) => t.key === rawTab)
    ? (rawTab as TabKey)
    : "overview";

  const selectTab = (key: TabKey) => {
    const next = new URLSearchParams(params);
    if (key === "overview") next.delete("tab");
    else next.set("tab", key);
    setParams(next, { replace: true });
  };

  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <h1 className="type-heading-xl">Clinical Admin</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          Operational queues and a customer-centric view of the clinical stack —
          EMR, prescriptions, pharmacy, billing, intake, and documents, one
          patient at a time.
        </p>
      </div>

      <div className="flex items-center gap-4 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => selectTab(t.key)}
            className={`type-label-sm px-12 py-10 -mb-px border-b-2 ${
              tab === t.key
                ? "border-ink text-ink"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <ClinicalAdminOverview />}
      {tab === "crm" && <CustomerTable />}
      {tab === "agent" && <AgentScreen />}
    </div>
  );
}

function ClinicalAdminOverview() {
  const destinations = [
    {
      to: "/admin/clinical-admin?tab=crm",
      title: "Patient CRM",
      description:
        "Search patients and review clinical, pharmacy, billing, intake, and document records.",
    },
    {
      to: "/admin/support",
      title: "Support board",
      description:
        "Work active support tickets, warnings, assignments, and clinician escalations.",
    },
    {
      to: "/admin/clinical-admin/reporting",
      title: "Reporting",
      description:
        "Generate patient funnel reports and load the operational lifecycle queues on demand.",
    },
    {
      to: "/admin/clinical-admin?tab=agent",
      title: "AI Agent",
      description:
        "Open the clinical operations agent for patient and order investigation.",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
      {destinations.map((destination) => (
        <Link
          key={destination.to}
          to={destination.to}
          className="p-16 border border-line rounded-lg bg-raised hover:bg-sunken transition-colors"
        >
          <h2 className="type-heading-md">{destination.title}</h2>
          <p className="type-body-sm text-ink-muted mt-6">
            {destination.description}
          </p>
        </Link>
      ))}
    </div>
  );
}
